import { CommonModule } from '@angular/common'
import {
    Component,
    ElementRef,
    Input,
    Renderer2,
    ViewChild,
    AfterViewInit,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    effect,
} from '@angular/core'
import { NavigationStart, Router } from '@angular/router'
import {
    ContentStackEvent,
    ContentStackItem,
    ContentStackService,
} from '@app/core/_services/content-stack.service'
import { UpcomingSessionsService } from '@app/left-panel/services/upcoming-sessions.service'
import { CalendarViewService } from '@app/routes/dashboard/services/calendar-view.service'
import { Drawer, DrawerModule } from 'primeng/drawer'
import { Subject, take, takeUntil } from 'rxjs'
import { UserProfileDynamicPanelWrapperComponent } from '../user-profile-dynamic-panel-wrapper/user-profile-dynamic-panel-wrapper.component'
import { UserAvailabilityListComponent } from '@app/routes/user/availability/user-availability.component'
import { ButtonModule } from 'primeng/button'
import { MenuDrawerStateService } from '@app/navigation/services/menu-drawer-state.service'
import { RpGroupMeetingDetailsComponent } from '../rp-group-meeting-details/rp-group-meeting-details.component'

@Component({
    selector: 'fm-mobile-drawer',
    imports: [CommonModule, DrawerModule, ButtonModule],
    templateUrl: './mobile-drawer.component.html',
    styleUrl: './mobile-drawer.component.scss',
    //changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileDrawerComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('mobileDrawerRef') drawerRef!: Drawer
    @Input({ required: true }) navRef!: ElementRef<HTMLElement>

    private startY = 0
    private currentY = 0
    private startX = 0
    private currentX = 0
    private dragging = false
    private readonly CLOSE_THRESHOLD = 100

    private panelEl!: HTMLElement | null

    private ngUnsubscribe = new Subject<void>()

    currentContent: ContentStackItem
    contentStackSize: number
    showBackButton: boolean
    backButtonLabel: string

    constructor(
        private router: Router,
        private calendarViewService: CalendarViewService,
        public upcomingSessionsService: UpcomingSessionsService,
        public contentStackService: ContentStackService,
        private cdr: ChangeDetectorRef,
        private menuDrawerState: MenuDrawerStateService,
    ) {
        effect(() => {
            const menuOpened = menuDrawerState.visible()

            if (menuOpened) {
                this.resetDrawer()
                this.contentStackService.clearContent()
                this.cdr.markForCheck()
            }
        })
    }

    resetDrawer(): void {
        this.startY = 0
        this.currentY = 0
        this.dragging = false
        this.panelEl = null
    }

    updateBackButtonStatus(): void {
        // NOTE: Stack size is updated after currentContent
        // Verify content exists before accessing it
        if (this.contentStackSize > 1 && this.currentContent) {
            if (this.currentContent.component === UserProfileDynamicPanelWrapperComponent) {
                this.showBackButton = true
                this.backButtonLabel = 'User profile'
            } else if (this.currentContent.component === UserAvailabilityListComponent) {
                this.showBackButton = true
                this.backButtonLabel = 'User availability'
            } else if (this.currentContent.component === RpGroupMeetingDetailsComponent) {
                this.showBackButton = true
                this.backButtonLabel = 'Group session details'
            } else {
                this.showBackButton = false
                this.backButtonLabel = null
            }
        } else {
            this.showBackButton = false
            this.backButtonLabel = null
        }
    }

    ngOnInit(): void {
        this.contentStackService.contentStackChanged$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event: ContentStackEvent) => {
                this.resetDrawer()
                this.currentContent = event.currentContent
                this.updateBackButtonStatus()
                this.cdr.markForCheck()
            })

        // The outlet re-applies its inputs on the next check; make sure one happens.
        this.contentStackService.contentContextUpdated$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((contentId) => {
                if (this.currentContent?.contentId === contentId) {
                    this.cdr.markForCheck()
                }
            })

        this.contentStackService.currentConstentStackSize$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((size) => {
                this.contentStackSize = size
                this.updateBackButtonStatus()
                this.cdr.markForCheck()
            })

        this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.resetDrawer()
                this.contentStackService.clearContent()
                this.updateBackButtonStatus()
                this.cdr.markForCheck()
            }
        })

        this.calendarViewService.triggerTodaySelected$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.resetDrawer()
                this.contentStackService.clearContent()
                this.cdr.markForCheck()
            })
    }

    /** Drawer overlay exists only *after* onShow fires */
    ngAfterViewInit(): void {
        this.drawerRef.onShow.pipe(take(1)).subscribe(() => {
            this.panelEl = this.drawerRef.container as HTMLElement
        })

        // safety: release reference once the drawer fully hides
        this.drawerRef.onHide.subscribe((e) => {
            this.resetDrawer()
            this.contentStackService.popContent()
        })
    }

    /* --------------------------------------------------
     *  Handle tap on “drawer bar” (instant close)
     * -------------------------------------------------- */
    clickDrawerHandle(e: Event): void {
        // NOTE: This will trigger onHide
        this.contentStackService.clearContent()
        this.drawerRef.close(e)
    }

    clickBackButton(e: Event): void {
        // NOTE: This will trigger onHide
        this.contentStackService.popContent()
    }

    /* --------------------------------------------------
     *  Touch event handlers for mobile browsers
     * -------------------------------------------------- */
    onTouchStart(ev: TouchEvent): void {
        if (ev.touches.length !== 1) return // Only handle single touch

        if (!this.panelEl) {
            this.panelEl = this.resolvePanel(ev as any)
            if (!this.panelEl) return
        }

        // Check if the touch target is in a scrollable element
        const target = ev.target as HTMLElement
        const isScrollable = this.isInScrollableElement(target)
        if (isScrollable) {
            return // Don't start dragging if touching a scrollable element
        }

        this.dragging = true
        this.startY = ev.touches[0].clientY
        this.startX = ev.touches[0].clientX
        this.currentY = 0
        this.currentX = 0

        // Don't prevent default here - let normal interactions work
    }

    onTouchMove(ev: TouchEvent): void {
        if (!this.dragging || !this.panel || ev.touches.length !== 1) return

        this.currentY = ev.touches[0].clientY - this.startY
        this.currentX = ev.touches[0].clientX - this.startX

        // Check if this is a vertical swipe (more vertical than horizontal)
        const isVerticalSwipe = Math.abs(this.currentY) > Math.abs(this.currentX)

        // Only handle downward swipes that are more vertical than horizontal
        if (this.currentY > 0 && isVerticalSwipe) {
            const translateY = this.currentY
            // Use direct DOM manipulation for better performance
            this.panel.style.transform = `translate3d(0, ${translateY}px, 0)`

            // Always prevent default for downward vertical swipes to stop scrolling
            ev.preventDefault()
        }
    }

    onTouchEnd(ev: TouchEvent): void {
        if (!this.dragging || !this.panel) return
        this.dragging = false

        // Only close if it's a downward swipe that meets the threshold
        const shouldClose = this.currentY >= this.CLOSE_THRESHOLD && this.currentY > 0

        this.panel.style.transition = 'transform 180ms ease-out'
        this.panel.style.transform = shouldClose
            ? 'translate3d(0, 100%, 0)'
            : 'translate3d(0, 0, 0)'

        setTimeout(() => {
            if (shouldClose) {
                this.contentStackService.clearContent()
                this.drawerRef.close(ev as any)
            }

            if (this.panel) {
                this.panel.style.transition = ''
            }
        }, 180)

        this.currentY = 0
    }

    /* --------------------------------------------------
     *  Helpers
     * -------------------------------------------------- */
    private get panel(): HTMLElement | null {
        return this.panelEl
    }

    private resolvePanel(ev: PointerEvent): HTMLElement | null {
        return (ev.target as HTMLElement).closest('.p-drawer')
    }

    private isInScrollableElement(element: HTMLElement): boolean {
        // Check if the element or any of its parents is scrollable
        let current = element
        while (current && current !== this.panelEl) {
            const style = window.getComputedStyle(current)
            const overflowY = style.overflowY
            const scrollHeight = current.scrollHeight
            const clientHeight = current.clientHeight

            // Check if element is scrollable (has overflow and content is larger than container)
            if ((overflowY === 'scroll' || overflowY === 'auto') && scrollHeight > clientHeight) {
                // If it's scrollable, check if it's scrolled to the top
                // Allow dragging if the element is at the top (scrollTop === 0)
                if (current.scrollTop > 0) {
                    return true // Can't drag if not at the top
                }
            }

            current = current.parentElement as HTMLElement
        }
        return false
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next()
        this.ngUnsubscribe.complete()
    }
}
